import { useState } from "react";
import { useTranslation } from "react-i18next";
import { hrApi, useLeaveBalances, useLeaveRequests, useLeaveTypes } from "../../hooks/hr";
import { useLocale } from "../../hooks/useLocale";
import { localName } from "../../lib/format";
import type { HrLevel, LeaveRequest } from "../../lib/typesHr";
import { Empty, SectionHeader } from "../ui/Badges";
import { Modal } from "../ui/Modal";
import { dateOnly, EmployeeSelect, Field, ReasonDialog, StatusPill, Table, todayIso, useAction, useEmpName } from "./shared";

/** File a leave request for yourself (employeeId omitted) or, as a manager / HR, for somebody else. */
export function LeaveRequestDialog({ open, onClose, forOthers = false }: { open: boolean; onClose: () => void; forOthers?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const types = useLeaveTypes();
  const { act, busy } = useAction();
  const [employeeId, setEmployeeId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [start, setStart] = useState(todayIso());
  const [end, setEnd] = useState(todayIso());
  const [startHalf, setStartHalf] = useState(false);
  const [endHalf, setEndHalf] = useState(false);
  const [reason, setReason] = useState("");
  const bal = useLeaveBalances(forOthers ? employeeId || null : undefined);
  const chosen = (types.data ?? []).find((x) => x.id === typeId);
  const balance = (bal.data ?? []).find((b) => b.leave_type_id === typeId);
  const valid = !!typeId && end >= start && (!forOthers || !!employeeId);

  async function submit() {
    const r = await act(() => hrApi.post<{ days: number }>("/leave/requests", { employeeId: forOthers ? employeeId : undefined, typeId, startDate: start, endDate: end, startHalfDay: startHalf, endHalfDay: endHalf, reason: reason.trim() || undefined }), "hr.leave.submitted");
    if (r) onClose();
  }
  return (
    <Modal open={open} title={t("hr.leave.newRequest")} onClose={onClose} footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={!valid || busy} onClick={() => void submit()}>{t("hr.leave.submit")}</button></>}>
      {forOthers && <Field label={t("hr.employee")}><EmployeeSelect value={employeeId} onChange={setEmployeeId} /></Field>}
      <Field label={t("hr.leave.type")}>
        <select className="field" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
          <option value="">{t("hr.leave.chooseType")}</option>
          {(types.data ?? []).filter((x) => x.active).map((x) => <option key={x.id} value={x.id}>{localName(x, locale)}</option>)}
        </select>
      </Field>
      {chosen?.tracks_balance && balance && <p className="rounded-xl bg-purple-50 px-3 py-2 text-xs font-bold text-cosmic-purple">{t("hr.leave.available", { days: balance.balance })}</p>}
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("hr.from")}><input type="date" className="field num" value={start} onChange={(e) => { setStart(e.target.value); if (e.target.value > end) setEnd(e.target.value); }} /></Field>
        <Field label={t("hr.to")}><input type="date" className="field num" value={end} min={start} onChange={(e) => setEnd(e.target.value)} /></Field>
      </div>
      <div className="flex gap-4 text-xs font-bold">
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={startHalf} onChange={(e) => setStartHalf(e.target.checked)} />{t("hr.leave.startHalf")}</label>
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={endHalf} onChange={(e) => setEndHalf(e.target.checked)} />{t("hr.leave.endHalf")}</label>
      </div>
      {chosen?.requires_document_after_days != null && <p className="text-[11px] font-semibold text-amber-700">{t("hr.leave.documentRule", { days: chosen.requires_document_after_days })}</p>}
      <Field label={t("hr.reason")}><textarea className="field min-h-16" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
    </Modal>
  );
}

export function LeaveTable({ rows, level, mine = false }: { rows: LeaveRequest[]; level: HrLevel; mine?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const name = useEmpName();
  const { act, busy } = useAction();
  const [cancelling, setCancelling] = useState<LeaveRequest | null>(null);
  const [rejecting, setRejecting] = useState<LeaveRequest | null>(null);
  const decide = (r: LeaveRequest, decision: "approve" | "reject", note?: string) =>
    act(() => hrApi.post("/decide", { entityType: "leave_request", entityId: r.id, decision, note }), decision === "approve" ? "hr.approved" : "hr.rejected");

  return (
    <>
      <Table head={[...(mine ? [] : [t("hr.employee")]), t("hr.leave.type"), t("hr.period"), t("hr.leave.days"), t("hr.status.label"), ""]} empty={rows.length === 0 ? <Empty>{t("hr.leave.none")}</Empty> : undefined}>
        {rows.map((r) => (
          <tr key={r.id}>
            {!mine && <td className="font-black">{name(r)} <span className="num font-mono text-[10px] text-gray-400">{r.employee_no}</span></td>}
            <td>{localName({ name: r.type_name, nameAr: r.type_name_ar }, locale)}</td>
            <td className="num">{dateOnly(r.start_date, locale)} → {dateOnly(r.end_date, locale)}</td>
            <td className="num font-bold">{r.days}</td>
            <td><StatusPill status={r.status} />{r.status === "pending" && r.waiting_for && <div className="mt-1 text-[10px] font-semibold text-gray-400">{t("hr.waitingFor", { role: t(`hr.role.${r.waiting_for}`, { defaultValue: r.waiting_for }) })}</div>}{r.decision_note && <div className="mt-1 text-[10px] text-gray-500">{r.decision_note}</div>}</td>
            <td className="whitespace-nowrap text-end">
              {r.status === "pending" && !mine && level >= 1 && (<><button type="button" className="btn-success me-1" disabled={busy} onClick={() => void decide(r, "approve")}>{t("hr.approve")}</button><button type="button" className="btn-ghost text-red-700" disabled={busy} onClick={() => setRejecting(r)}>{t("hr.reject")}</button></>)}
              {(r.status === "pending" && mine) || (r.status === "approved" && level >= 1) ? <button type="button" className="btn-ghost" disabled={busy} onClick={() => setCancelling(r)}>{r.status === "pending" ? t("hr.leave.withdraw") : t("hr.leave.cancel")}</button> : null}
            </td>
          </tr>
        ))}
      </Table>
      <ReasonDialog open={!!cancelling} title={t("hr.leave.cancelTitle")} confirmLabel={t("hr.confirm")} onClose={() => setCancelling(null)} onConfirm={(reason) => { const r = cancelling!; setCancelling(null); void act(() => hrApi.post(`/leave/requests/${r.id}/cancel`, { reason }), "hr.done"); }} />
      <ReasonDialog open={!!rejecting} title={t("hr.rejectTitle")} confirmLabel={t("hr.reject")} onClose={() => setRejecting(null)} onConfirm={(note) => { const r = rejecting!; setRejecting(null); void decide(r, "reject", note); }} />
    </>
  );
}

export function LeavePanel({ level }: { level: HrLevel }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("pending");
  const [open, setOpen] = useState(false);
  const reqs = useLeaveRequests({ status: status || undefined });
  const { act, busy } = useAction();
  const types = useLeaveTypes();

  return (
    <section className="card space-y-3 p-5" aria-label={t("hr.tabs.leave")}>
      <SectionHeader icon="🌴" title={t("hr.tabs.leave")} subtitle={t("hr.leave.subtitle")}
        right={<div className="flex flex-wrap items-center gap-2">
          <select className="field !w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t("hr.status.label")}>
            <option value="">{t("hr.all")}</option>
            {["pending", "approved", "rejected", "cancelled", "withdrawn"].map((s) => <option key={s} value={s}>{t(`hr.status.${s}`)}</option>)}
          </select>
          <button type="button" className="btn-primary" onClick={() => setOpen(true)}>{t("hr.leave.onBehalf")}</button>
        </div>} />
      <LeaveTable rows={reqs.data ?? []} level={level} />
      {level >= 3 && (types.data ?? []).length === 0 && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-dashed border-purple-300 bg-purple-50 p-3 text-xs font-bold text-cosmic-purple">
          {t("hr.leave.noTypes")}
          <button type="button" className="btn-primary" disabled={busy} onClick={() => void act(() => hrApi.post("/leave/seed-defaults"), "hr.done")}>{t("hr.leave.seed")}</button>
        </div>
      )}
      <LeaveRequestDialog open={open} onClose={() => setOpen(false)} forOthers />
    </section>
  );
}
